import API from 'shared/api/API';
import authenticationServiceApi from 'shared/api/authentication.service.api';
import userServiceApi from 'shared/api/user.service.api';
import { GENERIC_SERVER_ERROR_MESSAGE } from 'shared/constants/applicationErrorCodes';
import { REGIONS } from 'shared/constants/region.constants';
import UserService from 'shared/services/user.service';
import { jwtDecode } from 'jwt-decode';

const AUTHENTICATION_STORE_KEY = 'authentication_store';
class AuthenticationService {
  state = {};

  async init() {
    // Load tokens from local storage
    this.state = this._localStorage;

    // If there is region in the state - update the API client
    if (this.state.region) {
      API.setClientByRegion(this.state.region);
    }

    // If not authenticated
    if (!this.isAuthenticated) {
      await this.logout();
      return;
    }

    // Restore token on API client
    if (this.state.accessToken) {
      API.setToken(this.state.accessToken);
    }

    // If authenticated - perform me request
    const { error } = await userServiceApi.me();

    if (error?.response?.status === 401) {
      // If token is expired - logout
      await this.logout();
    }
  }

  /**
   * Sign in flow for the user
   *
   * @param {{
   *  email: string,
   * }} { email }
   * @return {{
   *  error?: string,
   *  data?: {
   *   id: string,
   *  },
   * }} { error, data }
   * @memberof AuthenticationService
   */
  async signInFlow({ email }) {
    const { region } = await UserService._selectRegion({ email });

    // The region is not found
    if (!region) {
      return { error: 'Region not found' };
    }

    // Update auth state with the region
    this._updateState({ region });

    // Sign in the user
    const { error, data } = await authenticationServiceApi.loginWithEmail({
      email,
    });

    if (error) {
      return { error: GENERIC_SERVER_ERROR_MESSAGE };
    }

    return { data };
  }

  /**
   * Logout flow for the user
   * @memberof AuthenticationService
   */
  async logout() {
    // if there is authentication state - clean cookies
    if (this.isAuthenticated) {
      await authenticationServiceApi.logout();
    }

    this._clearState();
  }

  /**
   * Authenticate the user
   * @param {{
   *  accessToken: string,
   *  refreshToken: string,
   *  user: {
   *   permissions: string,
   *   email: string,
   *   id: string,
   *  }
   * }} { accessToken, refreshToken, user }
   * @memberof AuthenticationService
   */
  updateAccessToken(accessToken) {
    const tokenDetails = jwtDecode(accessToken);
    this._updateState({ accessToken, tokenDetails });
    API.setToken(accessToken);
  }

  async authenticate({ accessToken, refreshToken, user }) {
    const refreshTokenDetails = jwtDecode(refreshToken);
    const tokenDetails = jwtDecode(accessToken);

    this._updateState({
      accessToken,
      refreshTokenDetails,
      tokenDetails,
      user,
    });
    API.setToken(accessToken);
  }

  /**
   * Update the state of the authentication service
   * @param {{
   *  tokenDetails: {
   *   exp: number,
   *   iat: number,
   *   sub: string,
   *  },
   *  refreshTokenDetails: {
   *    exp: number,
   *    iat: number,
   *    sub: string,
   *  },
   *  user: {
   *  permissions: string,
   *   email: string,
   *   id: string,
   *  }
   * }} newState
   * @private
   * @memberof AuthenticationService
   */
  _updateState(newState) {
    this.state = {
      ...this.state,
      ...newState,
    };

    localStorage.setItem(AUTHENTICATION_STORE_KEY, JSON.stringify(this.state));
  }

  /**
   * Clear the state of the authentication service
   * @memberof AuthenticationService
   */
  _clearState() {
    this.state = {};
    localStorage.removeItem(AUTHENTICATION_STORE_KEY);
  }

  get _localStorage() {
    try {
      return JSON.parse(localStorage.getItem(AUTHENTICATION_STORE_KEY)) || {};
    } catch (e) {
      return {};
    }
  }

  // ========== Auth methods =============
  // Same fallback as the API client, so this is always the region of the backend in use
  get region() {
    return this.state.region ?? REGIONS.US;
  }

  get tokenExpiration() {
    let { tokenDetails } = this.state;
    return 1000 * tokenDetails?.exp - Date.now();
  }

  get isTokenExpired() {
    let { tokenDetails } = this.state;
    if (tokenDetails && tokenDetails.exp) {
      return 1000 * tokenDetails.exp - Date.now() < 5000;
    }
    return true;
  }

  // Refresh token actions
  get refreshToken() {
    let { refreshToken } = this.state;

    return refreshToken;
  }

  get refreshTokenExpiration() {
    let { refreshTokenDetails } = this.state;
    return 1000 * refreshTokenDetails?.exp - Date.now();
  }

  get isRefreshTokenExpired() {
    let { refreshTokenDetails } = this.state;
    if (refreshTokenDetails && refreshTokenDetails.exp) {
      return 1000 * refreshTokenDetails.exp - Date.now() < 5000;
    }
    return this.isTokenExpired;
  }

  get isAuthenticated() {
    return !this.isRefreshTokenExpired;
  }

  async refreshAccessToken() {
    const { refreshTokenDetails, user } = this.state;

    if (!refreshTokenDetails) {
      return false;
    }

    try {
      const { data, error } = await authenticationServiceApi.refreshToken({
        userId: user?.id || refreshTokenDetails?.id,
        organizationId: user?.organizationId || refreshTokenDetails?.organizationId,
      });

      if (error || !data) {
        console.log('Failed to refresh access token');
        return false;
      }

      console.log('Token refresh succeeded');
      await this.authenticate(data);
      return true;
    } catch (e) {
      console.log('Failed to refresh access token:', e);
      return false;
    }
  }
}

export default new AuthenticationService();
